
import React, { useState } from "react";
import { useData } from "@/contexts/DataContext";
import MainLayout from "@/components/layouts/MainLayout";
import { DollarSign, Search, CalendarDays } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { SalesTable } from "@/components/reports/SalesTable";

import StatCard from "@/components/common/StatCard";
import MonthlySalesChart from "@/components/reports/MonthlySalesChart";

// Función reutilizable para formatear como moneda
function formatCurrency(amount: number) {
  return amount
    .toLocaleString("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 2,
    })
    .replace('COP', '$')
    .replace(/\s/g, "");
}

// Función para extraer el nombre de usuario del correo electrónico
const extractNameFromEmail = (email: string | null): string => {
  if (!email) return "Desconocido";
  
  // Extraer la parte antes del @ en el correo
  const nameMatch = email.match(/^([^@]+)@/);
  if (nameMatch && nameMatch[1]) {
    // Capitalizar la primera letra
    return nameMatch[1].charAt(0).toUpperCase() + nameMatch[1].slice(1);
  }
  
  return "Desconocido";
};

const Reports = () => {
  const { sales, ads, screens, currentMonthSales } = useData();
  const [selectedYear, setSelectedYear] = useState<string | null>("all");
  const [selectedMonth, setSelectedMonth] = useState<string | null>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>("all");

  // Obtener años únicos de las ventas
  const years = [...new Set(sales.map(sale => new Date(sale.date).getFullYear()))].sort().reverse();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // Obtener vendedores únicos
  const uniqueSellers = [...new Set(sales.map(sale => sale.sellerId))].map(sellerId => {
    const sale = sales.find(s => s.sellerId === sellerId);
    return {
      sellerId,
      sellerName: sale?.sellerName !== "Desconocido" ? 
        sale?.sellerName : 
        (sale?.sellerEmail ? extractNameFromEmail(sale.sellerEmail) : "Desconocido"),
      sellerEmail: sale?.sellerEmail || null,
    };
  });

  // Filtrar ventas según los filtros
  const filteredSales = sales.filter(sale => {
    // Aplicar filtro de búsqueda
    const matchSearch = searchTerm ? 
      sale.clientName.toLowerCase().includes(searchTerm.toLowerCase()) : 
      true;
    
    // Aplicar filtro de año
    const saleYear = new Date(sale.date).getFullYear();
    const matchYear = selectedYear === "all" ? true : saleYear.toString() === selectedYear;
    
    // Aplicar filtro de mes
    const saleMonth = new Date(sale.date).getMonth() + 1;
    const matchMonth = selectedMonth === "all" ? true : saleMonth.toString() === selectedMonth;
    
    // Aplicar filtro de vendedor
    const matchSeller = selectedSellerId === "all" ? true : sale.sellerId === selectedSellerId;
    
    return matchSearch && matchYear && matchMonth && matchSeller;
  });

  // SUMA TOTAL VENDIDO filtrado
  const totalVendido = filteredSales.reduce((acc, sale) => acc + (sale.amount || 0), 0);

  // Calcular ventas totales (históricas) y del mes actual
  const totalSales = sales.reduce((acc, sale) => acc + sale.amount, 0);
  const totalCurrentMonthSales = currentMonthSales.reduce((acc, sale) => acc + sale.amount, 0);

  // Formatear los totales de ventas
  const formattedTotalSales = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalSales);
  
  const formattedCurrentMonthSales = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS'
  }).format(totalCurrentMonthSales);

  // Agrupar ventas por vendedor para la tabla
  const salesBySellerData = uniqueSellers.map(seller => {
    const sellerSales = filteredSales.filter(sale => sale.sellerId === seller.sellerId);
    const totalAmount = sellerSales.reduce((sum, sale) => sum + sale.amount, 0);
    return {
      sellerId: seller.sellerId,
      sellerName: seller.sellerName,
      sellerEmail: seller.sellerEmail,
      totalAmount,
      salesCount: sellerSales.length
    };
  }).filter(seller => seller.salesCount > 0); // Solo mostrar vendedores con ventas

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="h-8 w-8 text-outdoor-600" />
            Facturación
          </h1>
          <p className="text-muted-foreground">
            Historial de todas las ventas realizadas por vendedor
          </p>
        </div>

        {/* Sales Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          <StatCard
            title="Ventas mes actual"
            value={formattedCurrentMonthSales}
            icon={<DollarSign size={24} className="text-green-600" />}
          />
          <StatCard
            title="Ventas históricas"
            value={formattedTotalSales}
            icon={<DollarSign size={24} className="text-blue-500" />}
          />
        </div>

        {/* Monthly Sales Chart */}
        <MonthlySalesChart />

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Buscar por nombre del cliente..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <Select value={selectedSellerId || "all"} onValueChange={setSelectedSellerId}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Todos los vendedores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los vendedores</SelectItem>
                {uniqueSellers.map(seller => (
                  <SelectItem key={seller.sellerId} value={seller.sellerId}>
                    {seller.sellerName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los años</SelectItem>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="flex items-center w-[120px]">
                <CalendarDays className="mr-2 h-4 w-4" />
                <SelectValue placeholder="Mes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los meses</SelectItem>
                {months.map(month => (
                  <SelectItem key={month} value={month.toString()}>
                    {format(new Date(2022, month - 1), 'MMMM', { locale: es })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sales Total Card */}
        <Card className="bg-gradient-to-br from-outdoor-700 to-outdoor-600 text-white">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-lg font-medium text-white">Total Facturado</div>
                <div className="text-sm text-white/80 mt-1">
                  {filteredSales.length} ventas registradas
                </div>
              </div>
              <div className="text-3xl font-bold">
                {formatCurrency(totalVendido)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales Table */}
        {salesBySellerData.length > 0 ? (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-xl font-bold mb-4">Ventas por Vendedor</h2>
              <SalesTable data={salesBySellerData} />
            </CardContent>
          </Card>
        ) : (
          <div className="text-center py-10">
            <p className="text-muted-foreground">No se encontraron ventas con los filtros seleccionados</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default Reports;
